import {
  CalendarOutlined,
  LoadingOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { Button, Col, Empty, Image, Modal, Row, Table, Typography } from "antd";
import Link from "antd/lib/typography/Link";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import { useParams } from "react-router-dom";
import SecondaryButton from "../Buttons/SecondaryButton";
import { MOODS } from "./constants";
import { createNotepadTableColumns, formatCalendarEventTime } from "./helpers";
import CalendarToolbar from "./Views/CalendarToolbar";

const localizer = momentLocalizer(moment);

import { formatDate } from "../../helpers/TableHelper";
import { translate } from "../../locale";
import LogBookServices from "../../services/LogBookServices";
import UserServices from "../../services/UserServices";

const { Title, Text } = Typography;

const NotepadCalendar = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [patient, setPatient] = useState(null);
  const [dataTable, setDataTable] = useState(null);
  const [modalDetails, setModalDetails] = useState(null);
  const [showAs, setShowAs] = useState("calendar");

  const { username } = useParams();

  useEffect(() => {
    loadPatientInfo();
  }, []);

  const loadPatientInfo = async () => {
    try {
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await LogBookServices.getNotepad(username, date);
      if (
        response.status >= 200 &&
        response.status <= 300 &&
        response.data.notes.length !== 0
      ) {
        let newData = response.data.notes.map((i) => {
          const registerDate = i.register_date;
          const registerDatePlusOne = new Date(registerDate);
          registerDatePlusOne.setHours(registerDatePlusOne.getHours() + 1);

          return {
            ...i,
            start: new Date(i.register_date),
            end: registerDatePlusOne,
            type: "cell",
          };
        });
        setDataTable(response.data.notes);
        setData(newData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const handleDetails = (item) => {
    setModalDetails(item);
  };

  const columns = createNotepadTableColumns(handleDetails, MOODS);

  const handleHideDetails = () => {
    setModalDetails(null);
  };

  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
  };
  const handleToday = () => {
    setDate((v) => moment().format("YYYY-MM-DD"));
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
    }
  };

  const getCalendarToolbar = (props) => {
    return (
      <CalendarToolbar
        {...props}
        onPrev={handlePrev}
        onToday={handleToday}
        onNext={handleNext}
      />
    );
  };

  const getCalendarWeekEvent = ({ event }) => {
    return (
      <Link
        className={`text-center w-full block ${
          event.type === "cell" ? "" : "rbc-event-content-header"
        }`}
        style={{ color: "white" }}
        onClick={(e) => {
          e.preventDefault();
          if (event.type === "cell") {
            handleDetails(event);
          }
        }}
      >
        {formatCalendarEventTime(event)}
      </Link>
    );
  };

  return (
    <Row className="w-full">
      <Col flex="column" className="w-full" id="Notepad">
        <Row className="w-full">
          <Col span={24} className="mt-4">
            <Title level={2} className="text-center">
              {translate("Notas de ")}
              {patient && `${patient.user.full_name}`}
            </Title>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={24} className="flex flex-row justify-end space-x-2">
            <span className="flex flex-col justify-center">
              {translate("Formato")}:
            </span>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowAs("calendar")}
              type={showAs === "calendar" ? "primary" : "default"}
            />
            <Button
              icon={<TableOutlined />}
              onClick={() => setShowAs("table")}
              type={showAs === "table" ? "primary" : "default"}
            />
          </Col>
        </Row>
        {showAs === "calendar" && (
          <Calendar
            localizer={localizer}
            defaultDate={null}
            defaultView={Views.WEEK}
            views={["week"]}
            events={data || []}
            startAccessor="start"
            endAccessor="end"
            step={60}
            timeslots={6}
            style={{ height: 500 }}
            components={{
              toolbar: getCalendarToolbar,
              week: {
                event: getCalendarWeekEvent,
              },
            }}
            formats={{
              eventTimeRangeFormat: () => "",
              timeGutterFormat: () => "",
            }}
          />
        )}
        {showAs === "table" && (
          <>
            <div className="flex flex-row my-4">
              <div>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handlePrev();
                  }}
                >
                  &lt;
                </SecondaryButton>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handleToday();
                  }}
                >
                  Hoy
                </SecondaryButton>
                <SecondaryButton
                  className=""
                  onClick={() => {
                    handleNext();
                  }}
                >
                  &gt;
                </SecondaryButton>
              </div>
            </div>
            <Table
              rowKey={(i) => i.id}
              className="w-full mt-6"
              dataSource={dataTable}
              columns={columns}
              loading={
                loading
                  ? { indicator: antIcon, className: "text-custom_blue" }
                  : false
              }
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
                filterTitle: translate("Filtros"),
                filterConfirm: translate("Aceptar"),
                filterReset: translate("Limpiar"),
                filterEmptyText: translate("No hay filtros"),
                filterCheckall: translate("Seleccinar todos"),
                filterSearchPlaceholder: translate("Buscar en filtros"),
              }}
            />
          </>
        )}
        <Modal
          width={600}
          open={modalDetails !== null}
          title={translate("Detalles del block de notas")}
          onCancel={handleHideDetails}
          footer={[
            <Button key="ok" type="primary" onClick={handleHideDetails}>
              {translate("Aceptar")}
            </Button>,
          ]}
        >
          {modalDetails !== null && (
            <Row>
              <Col className="flex-1">
                <Row>
                  <Col>
                    <Text>Fecha: {formatDate(modalDetails.register_date)}</Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      Hora: {formatDate(modalDetails.register_date, "HH:mm")}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      Estado de ánimo:{" "}
                      {modalDetails.mood ? MOODS[modalDetails.mood] : ""}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>{modalDetails.text}</Text>
                  </Col>
                </Row>
                {modalDetails.image_url && (
                  <Row>
                    <Col>
                      <Image
                        src={modalDetails.image_url}
                        style={{ maxHeight: window.innerHeight - 400 }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

NotepadCalendar.propTypes = {
  onNavigate: PropTypes.func,
};

export default NotepadCalendar;
